/**
 * 헤더 컴포넌트
 * 
 * 관리자 페이지 상단 헤더 영역
 * 제휴사 Admin 구조를 참고하여 본사 Admin에 맞게 구현
 * 
 * 작성일: 2025-12-01
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { MdSettings, MdLock, MdLogout } from 'react-icons/md';
import { getRoleBadgeColor, getRoleLabel } from '../../utils/helpers';
import './Header.css';

interface HeaderProps {
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('ko');
  const [logoError, setLogoError] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const languageMenuRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setShowLanguageMenu(false);
      }
    };

    if (showProfileMenu || showLanguageMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu, showLanguageMenu]);

  const handleLanguageChange = (lang: string) => {
    setCurrentLanguage(lang);
    setShowLanguageMenu(false);
    // TODO: 언어 변경 로직 구현
  };

  const getLanguageLabel = (lang: string): string => {
    const labels: { [key: string]: string } = {
      'ko': '한국어',
      'en': 'English',
      'ja': '日本語',
      'zh': '中文'
    };
    return labels[lang] || lang;
  };

  const handleLogout = async () => {
    if (window.confirm('로그아웃 하시겠습니까?')) {
      try {
        await logout();
        navigate('/login');
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
  };

  // 페이지 제목 가져오기
  const getPageTitle = (): string => {
    const path = location.pathname;
    const titleMap: { [key: string]: string } = {
      '/admin/dashboard': '대시보드',
      '/admin/bookings': '예약 관리',
      '/admin/bookings/calendar': '예약 캘린더',
      '/admin/tickets': '티켓 관리',
      '/admin/vouchers': '바우처 관리',
      '/admin/payments': '결제 관리',
      '/admin/settlements': '정산 관리',
      '/admin/settlements/agents': '에이전트 정산',
      '/admin/settlements/partners': '제휴업체 정산',
      '/admin/settlements/pg': 'PG사 정산',
      '/admin/agents': '에이전트 관리',
      '/admin/partners': '제휴업체 관리',
      '/admin/products': '상품 관리',
      '/admin/products/halls': '관 정보',
      '/admin/products/packages': '패키지 관리',
      '/admin/products/prices': '가격 관리',
      '/admin/promotions': '프로모션 관리',
      '/admin/fnb': 'F&B 관리',
      '/admin/employees': '직원 관리',
      '/admin/cctv': 'CCTV 관리',
      '/admin/content': '콘텐츠 관리',
      '/admin/content/notices': '공지사항',
      '/admin/content/faqs': 'FAQ',
      '/admin/content/events': '이벤트',
      '/admin/content/inquiries': '고객 문의',
      '/admin/admins': '관리자 관리',
      '/admin/admins/roles': '권한 그룹',
      '/admin/admins/permissions': '메뉴 권한',
      '/admin/admins/data-access-permissions': '데이터 접근 권한',
      '/admin/menus': '메뉴 관리',
      '/admin/menu-permissions': '메뉴 권한 설정',
      '/admin/reports': '통계 및 리포트',
    };

    // 동적 경로 처리 (예: /admin/bookings/:id)
    if (path.startsWith('/admin/bookings/') && path !== '/admin/bookings' && path !== '/admin/bookings/calendar') {
      return '예약 상세';
    }
    if (path.startsWith('/admin/payments/') && path !== '/admin/payments') {
      return '결제 상세';
    }
    if (path.startsWith('/admin/vouchers/') && path !== '/admin/vouchers') {
      return '바우처 상세';
    }
    if (path.startsWith('/admin/tickets/') && path !== '/admin/tickets') {
      return '티켓 상세';
    }
    if (path.startsWith('/admin/agents/') && path !== '/admin/agents') {
      return '에이전트 상세';
    }
    if (path.startsWith('/admin/partners/') && path !== '/admin/partners') {
      return '제휴업체 상세';
    }
    if (path.startsWith('/admin/products/halls/') && path !== '/admin/products/halls') {
      return '관 상세';
    }
    if (path.startsWith('/admin/products/packages/') && path !== '/admin/products/packages') {
      return '패키지 상세';
    }
    if (path.startsWith('/admin/promotions/') && path !== '/admin/promotions') {
      return '프로모션 상세';
    }
    if (path.startsWith('/admin/content/notices/') && path !== '/admin/content/notices') {
      return '공지사항 상세';
    }
    if (path.startsWith('/admin/content/faqs/') && path !== '/admin/content/faqs') {
      return 'FAQ 상세';
    }
    if (path.startsWith('/admin/content/events/') && path !== '/admin/content/events') {
      return '이벤트 상세';
    }
    if (path.startsWith('/admin/content/inquiries/') && path !== '/admin/content/inquiries') {
      return '문의 상세';
    }
    if (path.startsWith('/admin/admins/') && path !== '/admin/admins' && !path.startsWith('/admin/admins/roles') && !path.startsWith('/admin/admins/permissions') && !path.startsWith('/admin/admins/data-access-permissions')) {
      return '관리자 상세';
    }

    return titleMap[path] || '관리자';
  };

  // admin이 없거나 name이 없을 때 기본값 처리
  const getAvatarInitial = (): string => {
    if (user?.name && user.name.length > 0) {
      return user.name.charAt(0).toUpperCase();
    }
    return 'A'; // 기본값
  };

  const getDisplayName = (): string => {
    return user?.name || '관리자';
  };

  const getRoleName = (): string => {
    return user?.role || '관리자';
  };

  if (!user) {
    return null;
  }

  return (
    <Navbar className="admin-header" expand="lg" fixed="top">
      <div className="header-left">
        {/* 햄버거 메뉴 버튼 (모바일) */}
        <button className="menu-toggle-btn mobile-only" onClick={onMenuClick} aria-label="메뉴 토글">
          <span className="menu-icon">☰</span>
        </button>

        {/* 로고 */}
        <Link to="/admin/dashboard" className="header-logo">
          {!logoError ? (
            <img
              src="/images/logo.png"
              alt="Lumin Art 로고"
              className="header-logo-image"
              onError={() => setLogoError(true)}
            />
          ) : (
            <span className="logo-fallback">Lumin Art</span>
          )}
        </Link>

        {/* 페이지 제목 (Dash 스타일에서는 숨김) */}
        <h1 className="header-page-title">{getPageTitle()}</h1>
      </div>

      <div className="header-right">
        {/* 검색 아이콘 (Mobile) */}
        <button className="header-search-icon mobile-only" title="검색" aria-label="검색">
          <span className="search-icon">🔍</span>
        </button>

        {/* 알림 아이콘 (향후 구현) */}
        <div className="header-notifications desktop-only">
          <button className="notification-btn" title="알림" aria-label="알림">
            <span className="notification-icon">🔔</span>
            {/* <span className="notification-badge">3</span> */}
          </button>
        </div>

        {/* 언어 선택 */}
        <div className="header-language" ref={languageMenuRef}>
          <button
            className="language-btn"
            onClick={() => setShowLanguageMenu(!showLanguageMenu)}
            title="언어 선택"
            aria-label="언어 선택"
          >
            <span className="language-icon">🌐</span>
            <span className="desktop-only">{getLanguageLabel(currentLanguage)}</span>
          </button>

          {showLanguageMenu && (
            <div className="language-dropdown">
              <button
                className={`language-option ${currentLanguage === 'ko' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('ko')}
              >
                한국어
              </button>
              <button
                className={`language-option ${currentLanguage === 'en' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('en')}
              >
                English
              </button>
              <button
                className={`language-option ${currentLanguage === 'ja' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('ja')}
              >
                日本語
              </button>
              <button
                className={`language-option ${currentLanguage === 'zh' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('zh')}
              >
                中文
              </button>
            </div>
          )}
        </div>

        {/* 프로필 드롭다운 */}
        <div className="header-profile" ref={profileMenuRef}>
          <button className="profile-btn" onClick={() => setShowProfileMenu(!showProfileMenu)} aria-label="프로필 메뉴">
            <div className="profile-avatar">{getAvatarInitial()}</div>
            <span className="profile-name desktop-only">{getDisplayName()}</span>
            <span className={`role-badge ${getRoleBadgeColor(getRoleName())} desktop-only`}>
              {getRoleLabel(getRoleName())}
            </span>
            <span className="chevron-icon desktop-only">▼</span>
          </button>

          {showProfileMenu && (
            <div className="profile-dropdown">
              {/* 프로필 정보 헤더 */}
              <div className="profile-info">
                <div className="profile-avatar-large">{getAvatarInitial()}</div>
                <div className="profile-details">
                  <div className="profile-name-large">{getDisplayName()}</div>
                  <div className={`profile-role ${getRoleBadgeColor(getRoleName())}`}>
                    {getRoleLabel(getRoleName())}
                  </div>
                </div>
              </div>

              {/* 메뉴 액션 섹션 */}
              <div className="dropdown-section-wrapper">
                <div className="dropdown-divider" />
                <div className="dropdown-section">
                  <Link
                    to="/admin/profile"
                    className="dropdown-item action-item"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <span className="dropdown-icon">
                      {React.createElement(MdSettings as React.ComponentType<any>)}
                    </span>
                    <span className="dropdown-text">내 정보 수정</span>
                  </Link>
                  <Link
                    to="/admin/change-password"
                    className="dropdown-item action-item"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <span className="dropdown-icon">
                      {React.createElement(MdLock as React.ComponentType<any>)}
                    </span>
                    <span className="dropdown-text">비밀번호 변경</span>
                  </Link>
                </div>
              </div>

              {/* 로그아웃 섹션 */}
              <div className="dropdown-section-wrapper">
                <div className="dropdown-divider" />
                <button className="dropdown-item logout-item" onClick={handleLogout}>
                  <span className="dropdown-icon">
                    {React.createElement(MdLogout as React.ComponentType<any>)}
                  </span>
                  <span className="dropdown-text">로그아웃</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Navbar>
  );
};

export default Header;
